import { createSlice } from "@reduxjs/toolkit";
import { decodeJwtPayload } from "../utils/decodeJwtPayload";

const initialState = {
  /** Raw JWT from `/ssologin.action?jwtToken=`. */
  jwtToken: null,
  /** Decoded JWT payload claims. */
  decodedToken: null,
  /** Last SSO error message (from URL or validation). */
  errorMessage: null,
  /** Token expiry as UTC milliseconds (`Date.now()` comparable). */
  tokenExpiresAtMs: null,
};

/**
 * @param {Record<string, unknown> | null} decodedToken
 * @returns {number | null}
 */
export function getJwtExpiryMs(decodedToken) {
  const expSec = decodedToken?.exp;
  if (typeof expSec !== "number" || !Number.isFinite(expSec)) {
    return null;
  }
  return expSec * 1000;
}

/**
 * Valid when the JWT decodes and `exp` is still in the future vs system time.
 * @param {string | null | undefined} jwtToken
 * @param {Record<string, unknown> | null} [decodedToken]
 */
export function isSsoJwtValid(jwtToken, decodedToken = null) {
  if (!jwtToken || typeof jwtToken !== "string") {
    return false;
  }
  const decoded = decodedToken ?? decodeJwtPayload(jwtToken);
  if (!decoded) {
    return false;
  }
  const expiresAtMs = getJwtExpiryMs(decoded);
  if (expiresAtMs == null) {
    return false;
  }
  return expiresAtMs > Date.now();
}

function resolveUsernameFromDecoded(decodedToken) {
  if (!decodedToken || typeof decodedToken !== "object") {
    return null;
  }
  const candidates = [
    decodedToken.userName,
    decodedToken.username,
    decodedToken.UserName,
    decodedToken.preferred_username,
    decodedToken.unique_name,
    decodedToken.upn,
    decodedToken.email,
    decodedToken.sub,
    decodedToken.name,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text) {
      return text;
    }
  }
  return null;
}

const ssoAuthSlice = createSlice({
  name: "ssoAuth",
  initialState,
  reducers: {
    setSsoSession(state, { payload }) {
      const jwtToken = payload?.jwtToken ?? null;
      const decodedToken =
        payload?.decodedToken ?? (jwtToken ? decodeJwtPayload(jwtToken) : null);
      state.jwtToken = jwtToken;
      state.decodedToken = decodedToken;
      state.tokenExpiresAtMs = getJwtExpiryMs(decodedToken);
      state.errorMessage = null;
    },
    setSsoError(state, { payload }) {
      state.jwtToken = null;
      state.decodedToken = null;
      state.tokenExpiresAtMs = null;
      state.errorMessage =
        typeof payload === "string"
          ? payload
          : payload?.errorMessage ?? "Single Sign-On failed.";
    },
    clearSsoAuth: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder.addCase("auth/clearAuth", () => ({ ...initialState }));
  },
});

export const { setSsoSession, setSsoError, clearSsoAuth } = ssoAuthSlice.actions;
export const selectSsoAuth = (state) => state.ssoAuth;
export const selectSsoJwtToken = (state) => state.ssoAuth?.jwtToken ?? null;
export const selectSsoDecodedToken = (state) => state.ssoAuth?.decodedToken ?? null;
export const selectSsoErrorMessage = (state) => state.ssoAuth?.errorMessage ?? null;
export { resolveUsernameFromDecoded };
export default ssoAuthSlice.reducer;
